import { useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { TCompanyMatch } from './companyData';
import { CompanyLoading } from './CompanyLoading/CompanyLoading';
import { CompanyManualEntry } from './CompanyManualEntry/CompanyManualEntry';
import { CompanySearch } from './CompanySearch/CompanySearch';
import { CompanySelected } from './CompanySelected/CompanySelected';
import { fetchGetLegalForm } from '../../../../api/lendingApi/apiQueries';
import logger from '../../../../utils/logger';
import { TMobileSignupForm } from '../../form/form';

type TMode = 'search' | 'loading' | 'selected' | 'manual';

// DIT-680/681/682. Company step orchestrator: register search → loading →
// confirmation card, or manual entry (chosen from search or after a failed
// legal-form lookup). Each state is its own StepLayout-owning screen. Query
// lives here so deselecting a company returns to the prior search; the manual
// screen reads/writes the shared form store directly.
export const CompanyScreen = () => {
  const { setValue } = useFormContext<TMobileSignupForm>();
  const [mode, setMode] = useState<TMode>('search');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TCompanyMatch>();
  const [legalForm, setLegalForm] = useState<string>();

  // "Not listed" — carry the typed query over as the company name, then let
  // the user complete the rest manually.
  const enterManually = () => {
    setValue('companyName', query);
    setMode('manual');
  };

  const backToSearch = () => {
    // Clear the company fields and the query so search starts fresh.
    setValue('companyName', '');
    setValue('crefoId', '');
    setValue('street', '');
    setValue('buildingNumber', '');
    setValue('postcode', '');
    setValue('town', '');
    setValue('legalForm', undefined);
    setQuery('');
    setMode('search');
  };

  const handleSelect = async (company: TCompanyMatch) => {
    setSelected(company);
    setLegalForm(undefined);
    setMode('loading');

    // Persist the matched company into the form store. The legal form follows
    // from the separate lookup below; on failure the fields are already there
    // for the manual fall-through to complete.
    setValue('companyName', company.name);
    setValue('crefoId', company.crefo_id);
    setValue('buildingNumber', company.house_number);
    setValue('street', company.street);
    setValue('postcode', company.postcode);
    setValue('town', company.city);

    try {
      const response = await fetchGetLegalForm(company.crefo_id);

      if (!response?.data?.legal_form)
        throw new Error('No legal form returned');

      setValue('legalForm', response.data.legal_form);
      setLegalForm(response.data.legal_form);
      setMode('selected');
    } catch {
      logger.error('MobileSignup: legal form fetch failed', {
        tags: { team: 'de-introducer' },
        crefoId: company.crefo_id,
      });
      // Fall through to manual entry to complete the (already-seeded) details.
      setMode('manual');
    }
  };

  if (mode === 'loading') return <CompanyLoading />;

  if (mode === 'selected' && selected)
    return (
      <CompanySelected
        company={selected}
        legalForm={legalForm}
        onDeselect={() => {
          setSelected(undefined);
          setMode('search');
        }}
      />
    );

  if (mode === 'manual')
    return (
      <CompanyManualEntry
        onContinue={() => {
          // TODO(DIT-724): persist the company stage + advance.
        }}
        onBackToSearch={backToSearch}
      />
    );

  return (
    <CompanySearch
      query={query}
      onQueryChange={setQuery}
      onSelect={handleSelect}
      onEnterManually={enterManually}
    />
  );
};
